import { jwtDecode } from 'jwt-decode';

const DEFAULT_AUTH_BASE = 'https://tts-apim3.azure-api.net';
const AUTH_BASE = (process.env.REACT_APP_API_BASE_URL || DEFAULT_AUTH_BASE).replace(/\/$/, '');
const SESSION_KEY = 'session';
const TOKEN_KEY = 'idToken';

const buildUrl = (path) => {
  if (!path.startsWith('/')) {
    return `${AUTH_BASE}/${path}`;
  }
  return `${AUTH_BASE}${path}`;
};

const logInfo = (...args) => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.log('[authApi]', ...args);
  }
};

const logError = (...args) => {
  // eslint-disable-next-line no-console
  console.error('[authApi]', ...args);
};

const handleResponse = async (response) => {
  const contentType = response.headers.get('content-type');
  const isJson = contentType && contentType.includes('application/json');
  const payload = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const message = typeof payload === 'string' && payload
      ? payload
      : payload?.error_message || 'Authentication request failed';
    throw new Error(message);
  }

  return payload;
};

const safeSet = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    logError('Failed to persist localStorage value', error);
  }
};

const safeGet = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeRemove = (key) => {
  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    logError('Failed to remove localStorage key', error);
  }
};

const getCachedSession = () => {
  const cached = safeGet(SESSION_KEY);
  if (!cached) {
    return null;
  }
  try {
    return JSON.parse(cached);
  } catch (error) {
    logError('Invalid cached session; clearing', error);
    safeRemove(SESSION_KEY);
    return null;
  }
};

const cacheSession = (session) => {
  safeSet(SESSION_KEY, JSON.stringify(session));
};

const sendInitRequest = async (idToken) => {
  const response = await fetch(buildUrl('/init'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    credentials: 'include',
    body: JSON.stringify({ idToken }),
  });

  const data = await handleResponse(response);
  cacheSession(data);
  return data;
};

export const initSession = async (tokenOverride) => {
  const cached = getCachedSession();
  if (cached?.user_email) {
    return cached;
  }

  const idToken = tokenOverride || safeGet(TOKEN_KEY);
  if (!idToken) {
    logInfo('No ID token found for session init');
    return null;
  }

  try {
    return await sendInitRequest(idToken);
  } catch (error) {
    logError('initSession failed', error);
    return null;
  }
};

export const storeIdToken = (token) => safeSet(TOKEN_KEY, token);
export const getStoredIdToken = () => safeGet(TOKEN_KEY);
export const clearIdToken = () => safeRemove(TOKEN_KEY);
export const clearCachedSession = () => safeRemove(SESSION_KEY);

export const handleGoogleSignIn = async (credentialResponse, setUser) => {
  try {
    const token = credentialResponse?.credential;
    if (!token) {
      throw new Error('Missing Google credential');
    }

    const decoded = jwtDecode(token);
    logInfo('User signed in via Google', decoded);
    if (typeof setUser === 'function') {
      setUser(decoded);
    }
    storeIdToken(token);
    return token;
  } catch (error) {
    logError('Google Sign-In Error:', error);
    return null;
  }
};

export const handleGoogleSignOut = async (setUser, setError) => {
  try {
    if (typeof setUser === 'function') {
      setUser(null);
    }
    clearCachedSession();
    clearIdToken();
    logInfo('User signed out');
  } catch (error) {
    logError('Sign-Out Error:', error);
    if (typeof setError === 'function') {
      setError('Error signing out. Please try again.');
    }
  }
};

export const initSessionIfNeeded = async (passedToken = null, setSession) => {
  const cached = getCachedSession();
  if (cached?.user_email) {
    logInfo('Restored session from localStorage');
    if (typeof setSession === 'function') {
      setSession(cached);
    }
    return cached;
  }

  const idToken = passedToken || safeGet(TOKEN_KEY);
  if (!idToken) {
    logError('No ID token found for session init');
    return null;
  }

  try {
    const data = await sendInitRequest(idToken);
    logInfo('Session cookie set successfully');
    if (typeof setSession === 'function') {
      setSession(data);
    }
    return data;
  } catch (error) {
    logError('Error sending ID token to backend:', error);
    return null;
  }
};
